import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";

/**
 * MOCK spend per category. The taxonomy API returns `p__spend__sum = 0`, so we invent spend here:
 * every leaf gets a deterministic pseudo-random amount (stable per node id) and parents sum their children.
 */
export type TaxonomySpend = {
    spendById: ReadonlyMap<number, number>
    /** Highest spend at each depth, used to compare a category with the others on the same level */
    maxByDepth: readonly number[]
}

// Deterministic 0..1 value per node id (mulberry32 step)
function seeded(id: number): number {
    let t = (id * 0x6D2B79F5) >>> 0;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

const MIN_LEAF_SPEND = 5_000;
const MAX_LEAF_SPEND = 2_500_000;

// Cubed so a few categories dominate, like real spend
const leafSpend = (id: number) => Math.round(MIN_LEAF_SPEND + seeded(id) ** 3 * (MAX_LEAF_SPEND - MIN_LEAF_SPEND));

export function mockTaxonomySpend(root: m_taxonomy.Tree): TaxonomySpend {
    const spendById = new Map<number, number>();
    const maxByDepth: number[] = [];

    const visit = (n: m_taxonomy.Tree, depth: number): number => {
        const children = n.children.filter(c => !c.removed);
        const spend = children.length > 0
            ? children.reduce((sum, c) => sum + visit(c, depth + 1), 0)
            // A leaf keeps the spend of every category merged into it (its `sources`)
            : (n.sources.length > 0 ? n.sources : [n.id]).reduce((sum, id) => sum + leafSpend(id), 0);
        spendById.set(n.id, spend);
        maxByDepth[depth] = Math.max(maxByDepth[depth] ?? 0, spend);
        return spend;
    };
    visit(root, 0);
    return {spendById, maxByDepth};
}
